/**
 * Helpers tanggal Indonesia (Masehi) - timezone-safe (treat all dates as local-date strings).
 */

export const MONTH_NAMES_ID = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

export const MONTH_SLUGS_ID = [
  "januari", "februari", "maret", "april", "mei", "juni",
  "juli", "agustus", "september", "oktober", "november", "desember",
];

export const DAY_NAMES_ID = [
  "Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu",
];

export const DAY_NAMES_SHORT_ID = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

export function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

export function toISO(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** Parse "YYYY-MM-DD" -> {y,m,d} */
export function parseISO(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
}

/** Days in given month (1-12), Gregorian */
export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** 0=Sun..6=Sat for given Y/M/D (UTC-stable) */
export function dayOfWeek(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Format "DD MMMM YYYY" */
export function formatLong(year: number, month: number, day: number): string {
  return `${day} ${MONTH_NAMES_ID[month - 1]} ${year}`;
}

/** Slug "juni-2026" */
export function monthSlug(year: number, month: number): string {
  return `${MONTH_SLUGS_ID[month - 1]}-${year}`;
}

/** Slug "7-juni-2026" */
export function dateSlug(year: number, month: number, day: number): string {
  return `${day}-${MONTH_SLUGS_ID[month - 1]}-${year}`;
}

/**
 * Canonical calendar URL helpers. All Gregorian year/month/date pages live
 * under the `/kalender/` namespace (translated to `/en/calendar/` for the
 * English locale via the LocaleContext `href()` translator).
 *
 * Always pass these returned paths through `href(...)` so the locale prefix
 * gets applied correctly.
 */
export function calendarYearPath(year: number): string {
  return `/kalender/${year}`;
}

export function calendarMonthPath(year: number, month: number): string {
  return `/kalender/${year}/${MONTH_SLUGS_ID[month - 1]}`;
}

export function calendarDatePath(year: number, month: number, day: number): string {
  return `/kalender/${year}/${day}-${MONTH_SLUGS_ID[month - 1]}`;
}

/** Parse a bare month slug (no year), e.g. "mei" → 5. Returns null if invalid. */
export function parseBareMonthSlug(slug: string): number | null {
  if (!/^[a-z]+$/.test(slug)) return null;
  const idx = MONTH_SLUGS_ID.indexOf(slug);
  return idx >= 0 ? idx + 1 : null;
}

/** Parse a bare date slug (no year), e.g. "7-mei" → { d: 7, m: 5 }. */
export function parseBareDateSlug(slug: string): { d: number; m: number } | null {
  const m = /^(\d{1,2})-([a-z]+)$/.exec(slug);
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const idx = MONTH_SLUGS_ID.indexOf(m[2]);
  if (idx < 0) return null;
  if (d < 1 || d > 31) return null;
  return { d, m: idx + 1 };
}

/** EN equivalents (used by /en/calendar routes). */
export const MONTH_SLUGS_EN_LIST = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];
export function parseBareMonthSlugEn(slug: string): number | null {
  if (!/^[a-z]+$/.test(slug)) return null;
  const idx = MONTH_SLUGS_EN_LIST.indexOf(slug);
  return idx >= 0 ? idx + 1 : null;
}
export function parseBareDateSlugEn(slug: string): { d: number; m: number } | null {
  const m = /^(\d{1,2})-([a-z]+)$/.exec(slug);
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const idx = MONTH_SLUGS_EN_LIST.indexOf(m[2]);
  if (idx < 0) return null;
  if (d < 1 || d > 31) return null;
  return { d, m: idx + 1 };
}

/** Parse slug "juni-2026" -> { y, m } | null */
export function parseMonthSlug(slug: string): { y: number; m: number } | null {
  const m = /^([a-z]+)-(\d{4})$/.exec(slug);
  if (!m) return null;
  const monthIdx = MONTH_SLUGS_ID.indexOf(m[1]);
  if (monthIdx < 0) return null;
  const y = parseInt(m[2], 10);
  if (y < 1900 || y > 2100) return null;
  return { y, m: monthIdx + 1 };
}

/** Parse slug "7-juni-2026" -> { y, m, d } | null */
export function parseDateSlug(slug: string): { y: number; m: number; d: number } | null {
  // Accept "7-juni-2026"
  let m = /^(\d{1,2})-([a-z]+)-(\d{4})$/.exec(slug);
  if (m) {
    const d = parseInt(m[1], 10);
    const monthIdx = MONTH_SLUGS_ID.indexOf(m[2]);
    if (monthIdx < 0) return null;
    const y = parseInt(m[3], 10);
    if (y < 1900 || y > 2100) return null;
    const month = monthIdx + 1;
    if (d < 1 || d > daysInMonth(y, month)) return null;
    return { y, m: month, d };
  }
  // Accept ISO "2026-06-07"
  m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(slug);
  if (m) {
    const y = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (y < 1900 || y > 2100) return null;
    if (month < 1 || month > 12) return null;
    if (d < 1 || d > daysInMonth(y, month)) return null;
    return { y, m: month, d };
  }
  return null;
}

/** Year slug "2026" */
export function parseYearSlug(slug: string): number | null {
  if (!/^\d{4}$/.test(slug)) return null;
  const y = parseInt(slug, 10);
  if (y < 1900 || y > 2100) return null;
  return y;
}

/** Today as ISO in local timezone */
export function todayISO(): string {
  const now = new Date();
  return toISO(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Add days (UTC-safe). Returns ISO. */
export function addDays(dateISO: string, days: number): string {
  const p = parseISO(dateISO)!;
  const d = new Date(Date.UTC(p.y, p.m - 1, p.d));
  d.setUTCDate(d.getUTCDate() + days);
  return toISO(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** Difference in days between two ISO dates (b - a) */
export function diffDays(aISO: string, bISO: string): number {
  const a = parseISO(aISO)!;
  const b = parseISO(bISO)!;
  const da = Date.UTC(a.y, a.m - 1, a.d);
  const db = Date.UTC(b.y, b.m - 1, b.d);
  return Math.round((db - da) / 86400000);
}

/** Julian Day Number for civil Gregorian date. */
export function julianDay(year: number, month: number, day: number): number {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  );
}